"use client"

import { useState } from "react"
import Image from "next/image"
import { Check, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ROOM_THEME_MAPPINGS } from "@/src/utils/api-value-mappings"

interface StyleSelectorProps {
  selectedStyle: string
  onSelect: (style: string) => void
  showRoomTypeHelper?: boolean
}

const allStyles = ROOM_THEME_MAPPINGS.map((theme) => ({
  id: theme.value,
  name: theme.name,
  description: theme.name, // Using name as description since mappings don't have description
  image: `/${theme.image}`, // Add leading slash for proper Next.js path resolution
}))

const STYLES_PER_PAGE = 6

export function StyleSelector({ selectedStyle, onSelect, showRoomTypeHelper }: StyleSelectorProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const totalPages = Math.ceil(allStyles.length / STYLES_PER_PAGE)

  // Get styles for current page
  const currentStyles = allStyles.slice(currentPage * STYLES_PER_PAGE, (currentPage + 1) * STYLES_PER_PAGE)

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages)
  }

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Select Your Style</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Page {currentPage + 1} of {totalPages}
          </span>
        </div>
      </div>

      {showRoomTypeHelper && (
        <p className="text-sm text-gray-600 italic -mt-2">
          You can also change the room type you are designing by editing the Room Type displayed above.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentStyles.map((style) => (
          <div
            key={style.id}
            className={`flex flex-col border rounded-lg cursor-pointer transition-colors overflow-hidden ${
              selectedStyle === style.id
                ? "border-[#40A1AB] ring-2 ring-[#40A1AB]/20"
                : "border-gray-200 hover:border-[#40A1AB]/50"
            }`}
            onClick={() => onSelect(style.id)}
          >
            <div className="relative h-40 w-full">
              <Image src={style.image || "/placeholder.svg"} alt={style.name} fill className="object-cover" />
              {selectedStyle === style.id && (
                <div className="absolute top-2 right-2 bg-[#40A1AB] text-white p-1 rounded-full">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-medium">{style.name}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <Button
          variant="outline"
          size="icon"
          onClick={prevPage}
          className="h-9 w-9 rounded-full bg-transparent"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex gap-2">
          {Array.from({ length: totalPages }).map((_, index) => (
            <Button
              key={index}
              variant={currentPage === index ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentPage(index)}
              className={`w-9 h-9 p-0 ${currentPage === index ? "bg-[#40A1AB] hover:bg-[#3A919A]" : ""}`}
              aria-label={`Page ${index + 1}`}
              aria-current={currentPage === index ? "page" : undefined}
            >
              {index + 1}
            </Button>
          ))}
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={nextPage}
          className="h-9 w-9 rounded-full bg-transparent"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
